import React, { useEffect, useMemo, useState } from "react";
import { MdOutlineLocalPhone } from "react-icons/md";
import {
  Layout,
  Menu,
  message,
  Avatar,
  Modal,
  Dropdown,
  Button,
  notification,
  Grid,
  Drawer,
  Tooltip,
} from "antd";
import {
  AppstoreOutlined,
  PlusOutlined,
  LoginOutlined,
  LogoutOutlined,
  FormOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SkinOutlined,
  DownOutlined,
  UserOutlined,
  NotificationOutlined,
  DollarCircleOutlined,
  TagsOutlined,
  ColumnWidthOutlined,
  UnorderedListOutlined,
  FileDoneOutlined,
  BuildOutlined,
  FolderOpenOutlined,
  UserSwitchOutlined,
  SafetyCertificateOutlined,
  UserAddOutlined,
  SolutionOutlined,
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
  TruckOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import NotificationBell from "../components/NotificationBell";
import axiosInstance from "../utils/axiosInstance";
import ChatWidget from "../components/AdminChat";

const { Sider } = Layout;
const { SubMenu } = Menu;
const { useBreakpoint } = Grid;

const SidebarIcon = ({ children }) => <span className="sn-icon-wrap">{children}</span>;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [isLoggedIn] = useState(!!localStorage.getItem("isAuthenticated"));

  const userRole = localStorage.getItem("userRole") || "";
  const displayName =
    localStorage.getItem("userName") ||
    localStorage.getItem("username") ||
    localStorage.getItem("name") ||
    localStorage.getItem("fullName") ||
    localStorage.getItem("email") ||
    "Admin";
  const roleLabel = (userRole || "guest").toUpperCase();

  const isSuperAdmin = userRole === "super admin";
  const isAdmin = userRole === "admin";
  const isSuperAdminOrAdmin = isSuperAdmin || isAdmin;
  const isDeliveryUser = userRole === "deliveries";
  const isSalesUser = userRole === "sales";
  const isProductionAdmin = userRole === "production admin";
  const isProductionManager = userRole === "production manager";
  const isProdRole = isProductionAdmin || isProductionManager;

  // Sidebar width constants
  const SIDEBAR_WIDTH = 244;
  const SIDEBAR_COLLAPSED_WIDTH = 76;
  const sidebarWidth = isMobile ? 0 : collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  useEffect(() => {
    if (isDeliveryUser && (location.pathname === "/" || location.pathname.startsWith("/home"))) {
      navigate("/order-signed-delivery-note");
    }
    if (isSalesUser && (location.pathname === "/" || location.pathname.startsWith("/home"))) {
      navigate("/all-orders");
    }
    if (isProdRole && (location.pathname === "/" || location.pathname.startsWith("/home"))) {
      navigate("/production-dashboard");
    }
  }, [isDeliveryUser, isSalesUser, isProdRole, location.pathname, navigate]);

  useEffect(() => {
    if (
      isSalesUser &&
      (location.pathname.startsWith("/production-dashboard") ||
        location.pathname.startsWith("/production-orders"))
    ) {
      message.error("Access denied: production pages are not available for your role.");
      navigate("/all-orders");
    }
  }, [isSalesUser, location.pathname, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) navigate("/login");
  }, [navigate]);

  useEffect(() => {
    const interceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("isAuthenticated");
          localStorage.removeItem("token");
          localStorage.removeItem("userRole");
          message.error("Session expired. Please log in again.");
          navigate("/login");
        }
        return Promise.reject(error);
      }
    );
    return () => axiosInstance.interceptors.response.eject(interceptor);
  }, [navigate]);

  const selectedKey = useMemo(() => {
  const pathname = location.pathname;
  if (pathname.startsWith("/auto-orders")) return "auto-orders";
  if (pathname.startsWith("/admin-create-customer-order")) return "admin-create-customer-order";
  if (pathname.startsWith("/all-orders")) return "all-orders";
  if (pathname.startsWith("/customer-invoices")) return "customer-invoices";
  if (pathname.startsWith("/production-dashboard")) return "production-dashboard";
  if (pathname.startsWith("/order-signed-delivery-note")) return "order-signed-delivery-note";
  if (pathname.startsWith("/production-orders")) return "production-orders";
  if (pathname.startsWith("/deliveries-operation")) return "deliveries-operation";
  if (pathname.startsWith("/delivery-routes")) return "delivery-routes";
  if (pathname.startsWith("/home") || pathname === "/") return "home";
  if (pathname.startsWith("/products")) return "products";
  if (pathname.startsWith("/add-product")) return "add-product";
  if (pathname.startsWith("/add-new-bed")) return "add-new-bed";
  if (pathname.startsWith("/users")) return "users";
  if (pathname.startsWith("/requests")) return "requests";
  if (pathname.startsWith("/new-user-registration")) return "new-user-registration";
  if (pathname.startsWith("/add-new-employees")) return "add-new-employees";
  if (pathname.startsWith("/contact-us") || pathname.startsWith("/call-back-requests")) return "contact-us";
  if (pathname.startsWith("/variations")) return "variations";
  if (pathname.startsWith("/sizes")) return "sizes";
  if (pathname.startsWith("/categories")) return "categories";
  if (pathname.startsWith("/fabrics")) return "fabrics";
  if (pathname.startsWith("/storage-prices")) return "storage-prices";
  if (pathname.startsWith("/stockists-admin")) return "stockists-admin";
  if (pathname.startsWith("/news")) return "news";
  if (pathname.startsWith("/urgent-orders")) return "urgent-orders";
  if (pathname.startsWith("/downloads-admin")) return "downloads-admin";
  if (pathname.startsWith("/announcements-admin")) return "announcements-admin";
  if (pathname.startsWith("/faqs")) return "faqs";
  if (pathname.startsWith("/upcoming-products-admin")) return "upcoming-products-admin";
  if (pathname.startsWith("/complaints-admin")) return "complaints-admin";
  return "";
}, [location.pathname]);

 const openKeys = useMemo(() => {
  const pathname = location.pathname;
  const keys = [];
  if (
    pathname.startsWith("/all-orders") ||
    pathname.startsWith("/auto-orders") ||
    pathname.startsWith("/admin-create-customer-order") ||
    pathname.startsWith("/customer-invoices") ||
    pathname.startsWith("/order-signed-delivery-note") ||
    pathname.startsWith("/deliveries-operation") ||
    pathname.startsWith("/delivery-routes") ||
    pathname.startsWith("/urgent-orders")
  ) {
    keys.push("orders");
  }
  if (
    isProdRole &&
    (pathname.startsWith("/production-dashboard") || pathname.startsWith("/production-orders"))
  ) {
    keys.push("production");
  }
  const isUsersPath =
    pathname.startsWith("/users") ||
    pathname.startsWith("/requests") ||
    pathname.startsWith("/new-user-registration") ||
    pathname.startsWith("/add-new-employees");
  const isProductsCorePath =
    pathname.startsWith("/products") ||
    pathname.startsWith("/add-product") ||
    pathname.startsWith("/add-new-bed");
  const isCatalogPath =
    pathname.startsWith("/variations") ||
    pathname.startsWith("/sizes") ||
    pathname.startsWith("/categories") ||
    pathname.startsWith("/fabrics") ||
    pathname.startsWith("/storage-prices") ||
    pathname.startsWith("/stockists-admin");
  if ((isSuperAdminOrAdmin && isUsersPath) || isProductsCorePath || isCatalogPath) {
    keys.push("products-main");
    if (isProductsCorePath) {
      keys.push("products-core");
    } else {
      keys.push("products-catalog");
    }
  }
  return keys;
}, [location.pathname, isProdRole, isSuperAdminOrAdmin]);

  const handleMenuClick = ({ key }) => {
    if (isSalesUser && (key === "production-dashboard" || key === "production-orders")) {
      message.error("Access denied: production pages are not available for your role.");
      return;
    }
    if (key === "logout") return setLogoutModalVisible(true);
    if (key === "login") return navigate("/login");
    navigate(`/${key}`);
    if (isMobile) setDrawerVisible(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    notification.success({
      message: "Logged out successfully!",
      placement: "topRight",
      duration: 2.4,
    });
    setLogoutModalVisible(false);
    navigate("/login");
  };

  const userMenu = {
    items: [
      { key: "profile", icon: <UserOutlined />, label: "Profile" },
      { type: "divider" },
      { key: "logout", icon: <LogoutOutlined />, label: "Logout" },
    ],
    onClick: ({ key }) => {
      if (key === "profile") navigate("/profile");
      if (key === "logout") setLogoutModalVisible(true);
    },
  };

  const L = ({ title, children }) =>
    isMobile ? (
      <Tooltip title={title} placement="right">
        {children}
      </Tooltip>
    ) : (
      children
    );

  const icon = (node) => <SidebarIcon>{node}</SidebarIcon>;

  const sidebarContent = (
    <>
      {!collapsed || isMobile ? (
        <div className="sn-profile">
          <div className="sn-avatar-ring">
            <Avatar size={56} src="/profile.png" alt="Profile" />
          </div>
          <div className="sn-profile-name">{displayName}</div>
          <div className="sn-role-chip">
            <span className="sn-role-dot" />
            {roleLabel}
          </div>
        </div>
      ) : (
        <div
          style={{
            padding: "18px 0",
            display: "flex",
            justifyContent: "center",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div className="sn-avatar-ring" style={{ padding: 2 }}>
            <Avatar size={38} src="/profile.png" alt="Profile" />
          </div>
        </div>
      )}

      <div className="sn-scroll">
        {isProdRole ? (
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            defaultOpenKeys={["production"]}
            onClick={handleMenuClick}
            className="sn-menu"
            inlineCollapsed={!isMobile && collapsed}
            inlineIndent={14}
          >
            <SubMenu
              key="production"
              icon={icon(<BuildOutlined />)}
              title={
                <L title="Manage Production">
                  <span className="sn-label">Manage Production</span>
                </L>
              }
              className="sn-group sn-production"
            >
              <Menu.Item key="production-dashboard" className="sn-child">
                <L title="Production Dashboard">
                  <span className="sn-child-line">
                    <SidebarIcon><AppstoreOutlined /></SidebarIcon>
                    <span>Production Dashboard</span>
                  </span>
                </L>
              </Menu.Item>
              <Menu.Item key="production-orders" className="sn-child">
                <L title="Production Orders">
                  <span className="sn-child-line">
                    <SidebarIcon><BuildOutlined /></SidebarIcon>
                    <span>Production Orders</span>
                  </span>
                </L>
              </Menu.Item>
            </SubMenu>
          </Menu>
        ) : (
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            defaultOpenKeys={openKeys}
            onClick={handleMenuClick}
            className="sn-menu"
            inlineCollapsed={!isMobile && collapsed}
            inlineIndent={14}
          >
            {isDeliveryUser ? (
              <Menu.Item key="order-signed-delivery-note" icon={icon(<CheckCircleOutlined />)} className="sn-item sn-c-orders">
                <L title="Delivery Service">
                  <span className="sn-label">Delivery Service</span>
                </L>
              </Menu.Item>
            ) : isSalesUser ? (
              <SubMenu
                key="orders"
                icon={icon(<CheckCircleOutlined />)}
                title={
                  <L title="Manage Orders">
                    <span className="sn-label">Manage Orders</span>
                  </L>
                }
                className="sn-group sn-orders"
              >
                <Menu.Item key="all-orders" className="sn-child">
                  <L title="All Orders">
                    <span className="sn-child-line">
                      <SidebarIcon><UnorderedListOutlined /></SidebarIcon>
                      <span>All Orders</span>
                    </span>
                  </L>
                </Menu.Item>
                <Menu.Item key="deliveries-operation" className="sn-child">
                  <L title="Deliveries Operation">
                    <span className="sn-child-line">
                      <SidebarIcon><TruckOutlined /></SidebarIcon>
                      <span>Deliveries Operation</span>
                    </span>
                  </L>
                </Menu.Item>
                <Menu.Item key="order-signed-delivery-note" className="sn-child">
                  <L title="Delivery Service">
                    <span className="sn-child-line">
                      <SidebarIcon><FileDoneOutlined /></SidebarIcon>
                      <span>Delivery Service</span>
                    </span>
                  </L>
                </Menu.Item>
              </SubMenu>
            ) : (
              <>
                <Menu.Item key="home" icon={icon(<AppstoreOutlined />)} className="sn-item sn-c-home">
                  <L title="Home">
                    <span className="sn-label">Home</span>
                  </L>
                </Menu.Item>

                {isSuperAdminOrAdmin && (
                  <SubMenu
                    key="users"
                    icon={icon(<TeamOutlined />)}
                    title={
                      <L title="Users">
                        <span className="sn-label">Users</span>
                      </L>
                    }
                    className="sn-group sn-users"
                  >
                    <Menu.Item key="users" className="sn-child">
                      <L title="All Users">
                        <span className="sn-child-line">
                          <SidebarIcon><UserSwitchOutlined /></SidebarIcon>
                          <span>All Users</span>
                        </span>
                      </L>
                    </Menu.Item>
                    <Menu.Item key="requests" className="sn-child">
                      <L title="Approve Users">
                        <span className="sn-child-line">
                          <SidebarIcon><SafetyCertificateOutlined /></SidebarIcon>
                          <span>Approve Users</span>
                        </span>
                      </L>
                    </Menu.Item>
                    <Menu.Item key="new-user-registration" className="sn-child">
                      <L title="Add New Customer">
                        <span className="sn-child-line">
                          <SidebarIcon><UserAddOutlined /></SidebarIcon>
                          <span>Add New Customer</span>
                        </span>
                      </L>
                    </Menu.Item>
                    <Menu.Item key="add-new-employees" className="sn-child">
                      <L title="Add New Role">
                        <span className="sn-child-line">
                          <SidebarIcon><SolutionOutlined /></SidebarIcon>
                          <span>Add New Role</span>
                        </span>
                      </L>
                    </Menu.Item>
                  </SubMenu>
                )}

             <SubMenu
  key="orders"
  icon={icon(<CheckCircleOutlined />)}
  title={
    <L title="Manage Orders">
      <span className="sn-label">Manage Orders</span>
    </L>
  }
  className="sn-group sn-orders"
>
  <Menu.Item key="all-orders" className="sn-child">
    <L title="All Orders">
      <span className="sn-child-line">
        <SidebarIcon><UnorderedListOutlined /></SidebarIcon>
        <span>All Orders</span>
      </span>
    </L>
  </Menu.Item>

  {isSuperAdminOrAdmin && (
    <Menu.Item key="admin-create-customer-order" className="sn-child">
      <L title="Create Customer Order">
        <span className="sn-child-line">
          <SidebarIcon><PlusOutlined /></SidebarIcon>
          <span>Create Customer Order</span>
        </span>
      </L>
    </Menu.Item>
  )}

  {isSuperAdminOrAdmin && (
    <Menu.Item key="auto-orders" className="sn-child">
      <L title="Auto Orders">
        <span className="sn-child-line">
          <SidebarIcon><MailOutlined /></SidebarIcon>
          <span>Email Orders</span>
        </span>
      </L>
    </Menu.Item>
  )}

  {isSuperAdminOrAdmin && (
    <Menu.Item key="customer-invoices" className="sn-child">
      <L title="Customer Invoices">
        <span className="sn-child-line">
          <SidebarIcon><FileDoneOutlined /></SidebarIcon>
          <span>Customer Invoices</span>
        </span>
      </L>
    </Menu.Item>
  )}

  <Menu.Item key="deliveries-operation" className="sn-child">
    <L title="Deliveries Operation">
      <span className="sn-child-line">
        <SidebarIcon><TruckOutlined /></SidebarIcon>
        <span>Deliveries Operation</span>
      </span>
    </L>
  </Menu.Item>

  <Menu.Item key="order-signed-delivery-note" className="sn-child">
    <L title="Delivery Service">
      <span className="sn-child-line">
        <SidebarIcon><FileDoneOutlined /></SidebarIcon>
        <span>Delivery Service</span>
      </span>
    </L>
  </Menu.Item>

  <Menu.Item key="delivery-routes" className="sn-child">
    <L title="Delivery Routes">
      <span className="sn-child-line">
        <SidebarIcon><TruckOutlined /></SidebarIcon>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span>Delivery Routes</span>
          <span
            style={{
              padding: "2px 6px",
              borderRadius: 999,
              background: "#EAF3DE",
              color: "#3B6D11",
              fontSize: 10,
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            NEW
          </span>
        </span>
      </span>
    </L>
  </Menu.Item>

  <Menu.Item key="urgent-orders" className="sn-child sn-urgent">
    <L title="Urgent Orders">
      <span className="sn-child-line">
        <span style={{ fontSize: 15 }}>🚨</span>
        <span style={{ fontWeight: 700 }}>Urgent Orders</span>
      </span>
    </L>
  </Menu.Item>
</SubMenu>

                <SubMenu
                  key="production"
                  icon={icon(<BuildOutlined />)}
                  title={
                    <L title="Manage Production">
                      <span className="sn-label">Manage Production</span>
                    </L>
                  }
                  className="sn-group sn-production"
                >
                  <Menu.Item key="production-dashboard" className="sn-child">
                    <L title="Production Dashboard">
                      <span className="sn-child-line">
                        <SidebarIcon><AppstoreOutlined /></SidebarIcon>
                        <span>Production Dashboard</span>
                      </span>
                    </L>
                  </Menu.Item>
                  <Menu.Item key="production-orders" className="sn-child">
                    <L title="Production Orders">
                      <span className="sn-child-line">
                        <SidebarIcon><BuildOutlined /></SidebarIcon>
                        <span>Production Orders</span>
                      </span>
                    </L>
                  </Menu.Item>
                </SubMenu>

                <SubMenu
                  key="products-core"
                  icon={icon(<AppstoreOutlined />)}
                  title={<span className="sn-label">Products</span>}
                  className="sn-group sn-products"
                >
                  <Menu.Item key="products" className="sn-child">
                    <L title="All Products">
                      <span className="sn-child-line">
                        <SidebarIcon><AppstoreOutlined /></SidebarIcon>
                        <span>All Products</span>
                      </span>
                    </L>
                  </Menu.Item>
                  <Menu.Item key="add-new-bed" className="sn-child">
                    <L title="Add New Bed">
                      <span className="sn-child-line">
                        <SidebarIcon><PlusOutlined /></SidebarIcon>
                        <span>Add New Bed</span>
                      </span>
                    </L>
                  </Menu.Item>
                  <Menu.Item key="add-product" className="sn-child">
                    <L title="Add New Product">
                      <span className="sn-child-line">
                        <SidebarIcon><PlusOutlined /></SidebarIcon>
                        <span>Add New Product</span>
                      </span>
                    </L>
                  </Menu.Item>
                </SubMenu>

                <SubMenu
                  key="products-catalog"
                  icon={icon(<TagsOutlined />)}
                  title={<span className="sn-label">Catalog Settings</span>}
                  className="sn-group sn-catalog"
                >
                  <Menu.Item key="variations" className="sn-child">
                    <L title="Product Variations">
                      <span className="sn-child-line">
                        <SidebarIcon><FormOutlined /></SidebarIcon>
                        <span>Product Variations</span>
                      </span>
                    </L>
                  </Menu.Item>
                  <Menu.Item key="storage-prices" className="sn-child">
                    <L title="Manage Storage Prices">
                      <span className="sn-child-line">
                        <SidebarIcon><DollarCircleOutlined /></SidebarIcon>
                        <span>Storage Prices</span>
                      </span>
                    </L>
                  </Menu.Item>
                  <Menu.Item key="sizes" className="sn-child">
                    <L title="Manage Sizes">
                      <span className="sn-child-line">
                        <SidebarIcon><ColumnWidthOutlined /></SidebarIcon>
                        <span>Manage Sizes</span>
                      </span>
                    </L>
                  </Menu.Item>
                  <Menu.Item key="categories" className="sn-child">
                    <L title="Manage Categories">
                      <span className="sn-child-line">
                        <SidebarIcon><TagsOutlined /></SidebarIcon>
                        <span>Manage Categories</span>
                      </span>
                    </L>
                  </Menu.Item>
                  <Menu.Item key="fabrics" className="sn-child">
                    <L title="Manage Fabrics">
                      <span className="sn-child-line">
                        <SidebarIcon><SkinOutlined /></SidebarIcon>
                        <span>Manage Fabrics</span>
                      </span>
                    </L>
                  </Menu.Item>
                  <Menu.Item key="stockists-admin" className="sn-child">
                    <L title="Manage Stockists">
                      <span className="sn-child-line">
                        <SidebarIcon><AppstoreOutlined /></SidebarIcon>
                        <span>Manage Stockists</span>
                      </span>
                    </L>
                  </Menu.Item>
                </SubMenu>

                <Menu.Item key="contact-us" icon={icon(<MdOutlineLocalPhone />)} className="sn-item sn-c-callback">
                  <L title="Call Back Requests">
                    <span className="sn-label">Call Back Requests</span>
                  </L>
                </Menu.Item>

                {isSuperAdminOrAdmin && (
                  <Menu.Item key="news" icon={icon(<NotificationOutlined />)} className="sn-item sn-c-news">
                    <L title="News"><span className="sn-label">News</span></L>
                  </Menu.Item>
                )}
                {isSuperAdminOrAdmin && (
                  <Menu.Item key="announcements-admin" icon={icon(<NotificationOutlined />)} className="sn-item sn-c-announce">
                    <L title="Announcements"><span className="sn-label">Announcements</span></L>
                  </Menu.Item>
                )}
                {isSuperAdminOrAdmin && (
                  <Menu.Item key="faqs" icon={icon(<QuestionCircleOutlined />)} className="sn-item sn-c-faq">
                    <L title="FAQs"><span className="sn-label">FAQs</span></L>
                  </Menu.Item>
                )}
                {isSuperAdminOrAdmin && (
                  <Menu.Item key="downloads-admin" icon={icon(<FolderOpenOutlined />)} className="sn-item sn-c-downloads">
                    <L title="Downloads Manager"><span className="sn-label">Downloads Manager</span></L>
                  </Menu.Item>
                )}
                {isSuperAdminOrAdmin && (
                  <Menu.Item key="upcoming-products-admin" icon={icon(<AppstoreOutlined />)} className="sn-item sn-c-upcoming">
                    <L title="Upcoming Products"><span className="sn-label">Upcoming Products</span></L>
                  </Menu.Item>
                )}
                {isSuperAdminOrAdmin && (
                  <Menu.Item key="complaints-admin" icon={icon(<ExclamationCircleOutlined />)} className="sn-item sn-c-complaints">
                    <L title="Complaint Management"><span className="sn-label">Complaint Management</span></L>
                  </Menu.Item>
                )}
              </>
            )}
          </Menu>
        )}
      </div>

      <div className="sn-brand-footer">
        <span className="sn-brand-dot" />
        {(!collapsed || isMobile) && <span className="sn-brand-text">SERENA BEDS</span>}
      </div>
    </>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

        :root {
          --sn-bg-1: #08111f;
          --sn-bg-2: #0d1728;
          --sn-surface: rgba(255, 255, 255, 0.05);
          --sn-surface-2: rgba(255, 255, 255, 0.08);
          --sn-border: rgba(255, 255, 255, 0.08);
          --sn-text: rgba(255, 255, 255, 0.94);
          --sn-text-dim: rgba(255, 255, 255, 0.62);
          --sn-radius: 16px;

          --c-home: #f4c76a;
          --c-users: #8b9cff;
          --c-orders: #4ade80;
          --c-production: #c084fc;
          --c-products: #60a5fa;
          --c-catalog: #fb923c;
          --c-callback: #38bdf8;
          --c-news: #facc15;
          --c-announce: #f472b6;
          --c-faq: #a78bfa;
          --c-downloads: #2dd4bf;
          --c-upcoming: #67e8f9;
          --c-complaints: #f87171;

          --content-bg: #f6f8fc;
          --header-bg: rgba(255, 255, 255, 0.92);
          --header-border: rgba(15, 23, 42, 0.08);
          --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);
          --shadow-md: 0 10px 30px rgba(15, 23, 42, 0.12);

          --sidebar-width: ${SIDEBAR_WIDTH}px;
          --sidebar-collapsed-width: ${SIDEBAR_COLLAPSED_WIDTH}px;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes glowPulse {
          0%, 100% { opacity: 0.45; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.35); }
        }

        *, *::before, *::after {
          box-sizing: border-box;
        }

        html, body, #root {
          height: 100%;
          margin: 0;
          padding: 0;
          overflow-x: hidden;
        }

        /* ─── FIXED SIDEBAR ─── */
        .sn-fixed-sidebar {
          position: fixed !important;
          top: 0;
          left: 0;
          height: 100vh;
          z-index: 2000;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background:
            radial-gradient(circle at top, rgba(244, 199, 106, 0.18), transparent 36%),
            linear-gradient(180deg, var(--sn-bg-2), var(--sn-bg-1));
          border-right: 1px solid rgba(255, 255, 255, 0.06);
          box-shadow: 8px 0 32px rgba(0, 0, 0, 0.28);
          transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
          width: var(--sidebar-width);
        }

        .sn-fixed-sidebar.collapsed {
          width: var(--sidebar-collapsed-width);
        }

        /* Override Ant Design Sider styles when used inside fixed wrapper */
        .sn-fixed-sidebar .ant-layout-sider {
          background: transparent !important;
          box-shadow: none !important;
          border-right: none !important;
          position: static !important;
          height: 100% !important;
          flex: 1 1 auto;
          min-height: 0;
          overflow: hidden !important;
        }

        .sn-fixed-sidebar .ant-layout-sider-children {
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: hidden;
        }

        /* ─── PROFILE SECTION ─── */
        .sn-profile {
          padding: 22px 16px 18px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
          position: relative;
          background: linear-gradient(180deg, rgba(255,255,255,0.03), transparent);
          flex-shrink: 0;
        }

        .sn-profile::before {
          content: "";
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse 80% 70% at 50% 0%, rgba(244, 199, 106, 0.14), transparent 70%);
          pointer-events: none;
        }

        .sn-avatar-ring {
          position: relative;
          border-radius: 50%;
          padding: 3px;
          background: linear-gradient(135deg, #f4c76a, #fff1c1, #b38a2c);
          box-shadow: 0 0 22px rgba(244, 199, 106, 0.28);
        }

        .sn-profile-name {
          font-family: "DM Sans", sans-serif;
          font-size: 13px;
          font-weight: 700;
          color: var(--sn-text);
          text-align: center;
          letter-spacing: 0.01em;
        }

        .sn-role-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 999px;
          background: rgba(244, 199, 106, 0.12);
          border: 1px solid rgba(244, 199, 106, 0.28);
          font-family: "DM Sans", sans-serif;
          font-size: 10px;
          font-weight: 700;
          color: #f4c76a;
          letter-spacing: 0.15em;
          text-transform: uppercase;
        }

        .sn-role-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #f4c76a;
          box-shadow: 0 0 8px rgba(244, 199, 106, 0.85);
          animation: glowPulse 2s ease-in-out infinite;
        }

        /* ─── SCROLLABLE MENU AREA ─── */
        .sn-scroll {
          flex: 1 1 auto;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 10px 0 18px;
          min-height: 0;
        }

        .sn-scroll::-webkit-scrollbar {
          width: 4px;
        }

        .sn-scroll::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.12);
          border-radius: 999px;
        }

        .sn-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        /* ─── MENU ─── */
        .sn-menu,
        .sn-menu .ant-menu {
          background: transparent !important;
          border-inline-end: 0 !important;
        }

        .sn-icon-wrap {
          width: 18px;
          min-width: 18px;
          height: 18px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          line-height: 1;
          font-size: 15px;
        }

        .sn-child-line {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }

        .sn-menu .ant-menu-item,
        .sn-menu .ant-menu-submenu-title {
          margin: 4px 10px !important;
          padding: 0 12px !important;
          height: 44px !important;
          line-height: 44px !important;
          display: flex !important;
          align-items: center !important;
          gap: 10px !important;
          border-radius: 14px !important;
          color: var(--sn-text-dim) !important;
          width: calc(100% - 20px) !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
          position: relative;
          overflow: hidden;
          backdrop-filter: blur(8px);
        }

        .sn-menu .ant-menu-submenu-title {
          padding-right: 36px !important;
        }

        .sn-menu .ant-menu-submenu-arrow {
          right: 12px !important;
          color: rgba(255, 255, 255, 0.28) !important;
        }

        .sn-menu .ant-menu-title-content {
          flex: 1 1 auto;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sn-label {
          font-family: "DM Sans", sans-serif;
          font-size: 13.5px;
          font-weight: 500;
          letter-spacing: 0.01em;
        }

        .sn-menu .ant-menu-item .anticon,
        .sn-menu .ant-menu-submenu-title .anticon {
          font-size: 15px !important;
          flex-shrink: 0;
        }

        .sn-menu .ant-menu-item:hover,
        .sn-menu .ant-menu-submenu-title:hover {
          color: rgba(255, 255, 255, 0.96) !important;
          background: rgba(255, 255, 255, 0.08) !important;
          transform: translateX(1px);
        }

        .sn-menu .sn-item:hover::before,
        .sn-menu .sn-item.ant-menu-item-selected::before {
          content: "";
          position: absolute;
          left: 0;
          top: 8px;
          bottom: 8px;
          width: 3px;
          border-radius: 0 3px 3px 0;
        }

        .sn-menu .sn-c-home:hover,
        .sn-menu .sn-c-home.ant-menu-item-selected {
          background: rgba(244, 199, 106, 0.12) !important;
          color: var(--c-home) !important;
        }
        .sn-menu .sn-c-home.ant-menu-item-selected::before { background: var(--c-home); }
        .sn-menu .sn-c-home .anticon { color: var(--c-home) !important; }

        .sn-menu .sn-c-callback:hover,
        .sn-menu .sn-c-callback.ant-menu-item-selected {
          background: rgba(56, 189, 248, 0.12) !important;
          color: var(--c-callback) !important;
        }
        .sn-menu .sn-c-callback.ant-menu-item-selected::before { background: var(--c-callback); }
        .sn-menu .sn-c-callback .anticon { color: var(--c-callback) !important; }

        .sn-menu .sn-c-news:hover,
        .sn-menu .sn-c-news.ant-menu-item-selected {
          background: rgba(250, 204, 21, 0.12) !important;
          color: var(--c-news) !important;
        }
        .sn-menu .sn-c-news.ant-menu-item-selected::before { background: var(--c-news); }
        .sn-menu .sn-c-news .anticon { color: var(--c-news) !important; }

        .sn-menu .sn-c-announce:hover,
        .sn-menu .sn-c-announce.ant-menu-item-selected {
          background: rgba(244, 114, 182, 0.12) !important;
          color: var(--c-announce) !important;
        }
        .sn-menu .sn-c-announce.ant-menu-item-selected::before { background: var(--c-announce); }
        .sn-menu .sn-c-announce .anticon { color: var(--c-announce) !important; }

        .sn-menu .sn-c-faq:hover,
        .sn-menu .sn-c-faq.ant-menu-item-selected {
          background: rgba(167, 139, 250, 0.12) !important;
          color: var(--c-faq) !important;
        }
        .sn-menu .sn-c-faq.ant-menu-item-selected::before { background: var(--c-faq); }
        .sn-menu .sn-c-faq .anticon { color: var(--c-faq) !important; }

        .sn-menu .sn-c-downloads:hover,
        .sn-menu .sn-c-downloads.ant-menu-item-selected {
          background: rgba(45, 212, 191, 0.12) !important;
          color: var(--c-downloads) !important;
        }
        .sn-menu .sn-c-downloads.ant-menu-item-selected::before { background: var(--c-downloads); }
        .sn-menu .sn-c-downloads .anticon { color: var(--c-downloads) !important; }

        .sn-menu .sn-c-upcoming:hover,
        .sn-menu .sn-c-upcoming.ant-menu-item-selected {
          background: rgba(103, 232, 249, 0.12) !important;
          color: var(--c-upcoming) !important;
        }
        .sn-menu .sn-c-upcoming.ant-menu-item-selected::before { background: var(--c-upcoming); }
        .sn-menu .sn-c-upcoming .anticon { color: var(--c-upcoming) !important; }

        .sn-menu .sn-c-complaints:hover,
        .sn-menu .sn-c-complaints.ant-menu-item-selected {
          background: rgba(248, 113, 113, 0.12) !important;
          color: var(--c-complaints) !important;
        }
        .sn-menu .sn-c-complaints.ant-menu-item-selected::before { background: var(--c-complaints); }
        .sn-menu .sn-c-complaints .anticon { color: var(--c-complaints) !important; }

        .sn-menu .sn-users > .ant-menu-submenu-title { color: rgba(255,255,255,0.82) !important; }
        .sn-menu .sn-users > .ant-menu-submenu-title .anticon { color: var(--c-users) !important; }
        .sn-menu .sn-users > .ant-menu-submenu-title:hover,
        .sn-menu .sn-users.ant-menu-submenu-open > .ant-menu-submenu-title {
          background: rgba(139, 156, 255, 0.1) !important;
          color: var(--c-users) !important;
        }

        .sn-menu .sn-orders > .ant-menu-submenu-title { color: rgba(255,255,255,0.82) !important; }
        .sn-menu .sn-orders > .ant-menu-submenu-title .anticon { color: var(--c-orders) !important; }
        .sn-menu .sn-orders > .ant-menu-submenu-title:hover,
        .sn-menu .sn-orders.ant-menu-submenu-open > .ant-menu-submenu-title {
          background: rgba(74, 222, 128, 0.1) !important;
          color: var(--c-orders) !important;
        }

        .sn-menu .sn-production > .ant-menu-submenu-title { color: rgba(255,255,255,0.82) !important; }
        .sn-menu .sn-production > .ant-menu-submenu-title .anticon { color: var(--c-production) !important; }
        .sn-menu .sn-production > .ant-menu-submenu-title:hover,
        .sn-menu .sn-production.ant-menu-submenu-open > .ant-menu-submenu-title {
          background: rgba(192, 132, 252, 0.1) !important;
          color: var(--c-production) !important;
        }

        .sn-menu .sn-products > .ant-menu-submenu-title { color: rgba(255,255,255,0.82) !important; }
        .sn-menu .sn-products > .ant-menu-submenu-title .anticon { color: var(--c-products) !important; }
        .sn-menu .sn-products > .ant-menu-submenu-title:hover,
        .sn-menu .sn-products.ant-menu-submenu-open > .ant-menu-submenu-title {
          background: rgba(96, 165, 250, 0.1) !important;
          color: var(--c-products) !important;
        }

        .sn-menu .sn-catalog > .ant-menu-submenu-title { color: rgba(255,255,255,0.82) !important; }
        .sn-menu .sn-catalog > .ant-menu-submenu-title .anticon { color: var(--c-catalog) !important; }
        .sn-menu .sn-catalog > .ant-menu-submenu-title:hover,
        .sn-menu .sn-catalog.ant-menu-submenu-open > .ant-menu-submenu-title {
          background: rgba(251, 146, 60, 0.1) !important;
          color: var(--c-catalog) !important;
        }

        .sn-menu .ant-menu-sub {
          background: transparent !important;
        }

        li.ant-menu-submenu.sn-users > ul.ant-menu,
        li.ant-menu-submenu.sn-orders > ul.ant-menu,
        li.ant-menu-submenu.sn-production > ul.ant-menu,
        li.ant-menu-submenu.sn-products > ul.ant-menu,
        li.ant-menu-submenu.sn-catalog > ul.ant-menu {
          margin: 0 10px 6px !important;
          border-radius: 14px !important;
          overflow: hidden;
          padding: 5px 0 !important;
          backdrop-filter: blur(10px);
        }

        li.ant-menu-submenu.sn-users > ul.ant-menu {
          background: rgba(139, 156, 255, 0.06) !important;
          border: 1px solid rgba(139, 156, 255, 0.12) !important;
        }
        li.ant-menu-submenu.sn-orders > ul.ant-menu {
          background: rgba(74, 222, 128, 0.06) !important;
          border: 1px solid rgba(74, 222, 128, 0.12) !important;
        }
        li.ant-menu-submenu.sn-production > ul.ant-menu {
          background: rgba(192, 132, 252, 0.06) !important;
          border: 1px solid rgba(192, 132, 252, 0.12) !important;
        }
        li.ant-menu-submenu.sn-products > ul.ant-menu {
          background: rgba(96, 165, 250, 0.06) !important;
          border: 1px solid rgba(96, 165, 250, 0.12) !important;
        }
        li.ant-menu-submenu.sn-catalog > ul.ant-menu {
          background: rgba(251, 146, 60, 0.06) !important;
          border: 1px solid rgba(251, 146, 60, 0.12) !important;
        }

        li.ant-menu-submenu.sn-users > ul.ant-menu .sn-child,
        li.ant-menu-submenu.sn-orders > ul.ant-menu .sn-child,
        li.ant-menu-submenu.sn-production > ul.ant-menu .sn-child,
        li.ant-menu-submenu.sn-products > ul.ant-menu .sn-child,
        li.ant-menu-submenu.sn-catalog > ul.ant-menu .sn-child {
          color: rgba(255,255,255,0.68) !important;
          margin: 2px 6px !important;
          width: calc(100% - 12px) !important;
          border-radius: 10px !important;
        }

        li.ant-menu-submenu.sn-users > ul.ant-menu .sn-child:hover { background: rgba(139,156,255,0.12) !important; color: var(--c-users) !important; }
        li.ant-menu-submenu.sn-users > ul.ant-menu .sn-child.ant-menu-item-selected { background: rgba(139,156,255,0.18) !important; color: var(--c-users) !important; }

        li.ant-menu-submenu.sn-orders > ul.ant-menu .sn-child:hover { background: rgba(74,222,128,0.12) !important; color: var(--c-orders) !important; }
        li.ant-menu-submenu.sn-orders > ul.ant-menu .sn-child.ant-menu-item-selected { background: rgba(74,222,128,0.18) !important; color: var(--c-orders) !important; }

        li.ant-menu-submenu.sn-production > ul.ant-menu .sn-child:hover { background: rgba(192,132,252,0.12) !important; color: var(--c-production) !important; }
        li.ant-menu-submenu.sn-production > ul.ant-menu .sn-child.ant-menu-item-selected { background: rgba(192,132,252,0.18) !important; color: var(--c-production) !important; }

        li.ant-menu-submenu.sn-products > ul.ant-menu .sn-child:hover { background: rgba(96,165,250,0.12) !important; color: var(--c-products) !important; }
        li.ant-menu-submenu.sn-products > ul.ant-menu .sn-child.ant-menu-item-selected { background: rgba(96,165,250,0.18) !important; color: var(--c-products) !important; }

        li.ant-menu-submenu.sn-catalog > ul.ant-menu .sn-child:hover { background: rgba(251,146,60,0.12) !important; color: var(--c-catalog) !important; }
        li.ant-menu-submenu.sn-catalog > ul.ant-menu .sn-child.ant-menu-item-selected { background: rgba(251,146,60,0.18) !important; color: var(--c-catalog) !important; }

        .sn-urgent {
          color: rgba(251, 191, 36, 0.95) !important;
        }
        .sn-urgent:hover {
          background: rgba(251, 191, 36, 0.1) !important;
          color: #fbbf24 !important;
        }
        .sn-urgent.ant-menu-item-selected {
          background: rgba(251, 191, 36, 0.16) !important;
          color: #fbbf24 !important;
        }

        /* Collapsed menu overrides */
        .ant-menu-inline-collapsed .ant-menu-title-content,
        .ant-menu-inline-collapsed .sn-label,
        .ant-menu-inline-collapsed .sn-child-line > span:last-child {
          display: none !important;
          width: 0 !important;
          opacity: 0 !important;
          visibility: hidden !important;
        }

        .ant-menu-inline-collapsed .ant-menu-item,
        .ant-menu-inline-collapsed .ant-menu-submenu-title {
          padding-inline: 0 !important;
          justify-content: center !important;
          overflow: hidden !important;
        }

        .ant-menu-inline-collapsed .sn-child-line {
          width: 100% !important;
          justify-content: center !important;
          gap: 0 !important;
        }

        .ant-menu-inline-collapsed .sn-child-line .sn-icon-wrap {
          margin: 0 !important;
        }

        .ant-menu-inline-collapsed .ant-menu-submenu-arrow {
          display: none !important;
        }

        .ant-menu-inline-collapsed .sn-menu .ant-menu-item,
        .ant-menu-inline-collapsed .sn-menu .ant-menu-submenu-title {
          margin: 4px 8px !important;
          width: calc(100% - 16px) !important;
        }

        .ant-menu-inline-collapsed .sn-menu .ant-menu-item .anticon,
        .ant-menu-inline-collapsed .sn-menu .ant-menu-submenu-title .anticon {
          margin: 0 !important;
          font-size: 18px !important;
        }

        /* ─── BRAND FOOTER ─── */
        .sn-brand-footer {
          padding: 16px;
          border-top: 1px solid rgba(255,255,255,0.06);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          flex-shrink: 0;
          background: linear-gradient(180deg, transparent, rgba(255,255,255,0.03));
        }

        .sn-brand-text {
          font-family: "Playfair Display", Georgia, serif;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.14em;
          color: rgba(244, 199, 106, 0.75);
          white-space: nowrap;
        }

        .sn-brand-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f4c76a, #fff1c1);
          box-shadow: 0 0 10px rgba(244, 199, 106, 0.7);
          flex-shrink: 0;
        }

        /* ─── MAIN CONTENT WRAPPER ─── */
        .sn-main-wrapper {
          transition: margin-left 0.22s cubic-bezier(0.4, 0, 0.2, 1);
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: var(--content-bg);
        }

        /* ─── HEADER ─── */
        .sn-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 62px;
  background: var(--header-bg);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--header-border);
  padding: 0 20px;
  position: sticky;
  top: 0;
  z-index: 3000;
  box-shadow: var(--shadow-sm);
  flex-shrink: 0;
  overflow: visible;
}

        .sn-header-left {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .sn-header-right {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .sn-header-divider {
          width: 1px;
          height: 22px;
          background: linear-gradient(to bottom, transparent, rgba(15, 23, 42, 0.18), transparent);
        }

        .sn-toggle-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border-radius: 12px;
          cursor: pointer;
          color: #64748b;
          transition: all 0.18s ease;
          background: #fff;
          border: 1px solid rgba(15, 23, 42, 0.06);
          box-shadow: var(--shadow-sm);
        }

        .sn-toggle-btn:hover {
          background: #f8fafc;
          color: #0f172a;
          transform: translateY(-1px);
        }

        .sn-profile-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 5px 11px 5px 5px;
          border-radius: 999px;
          cursor: pointer;
          transition: all 0.18s ease;
          background: rgba(255,255,255,0.6);
          border: 1px solid rgba(15,23,42,0.06);
          box-shadow: var(--shadow-sm);
        }

        .sn-profile-btn:hover {
          background: #fff;
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }

        .sn-profile-info {
          display: flex;
          flex-direction: column;
          line-height: 1.1;
          text-align: left;
        }

        .sn-profile-name-sm {
          font-family: "DM Sans", sans-serif;
          font-size: 13px;
          font-weight: 700;
          color: #0f172a;
        }

        .sn-profile-role-sm {
          font-family: "DM Sans", sans-serif;
          font-size: 10px;
          color: #64748b;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .sn-notif-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border-radius: 12px;
          cursor: pointer;
          color: #64748b;
          transition: all 0.18s ease;
          background: rgba(255,255,255,0.6);
          border: 1px solid rgba(15,23,42,0.06);
          box-shadow: var(--shadow-sm);
        }

        .sn-notif-btn:hover {
          background: #fff;
          color: #0f172a;
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }

        /* ─── CONTENT AREA ─── */
        .sn-content-area {
          flex: 1 1 auto;
          background: var(--content-bg);
          overflow-y: auto;
        }

        .sn-content-inner {
          padding: 20px 24px;
          animation: fadeUp 0.25s ease both;
        }

        /* ─── MODALS / DRAWERS ─── */
        .ant-modal-confirm-title,
        .ant-modal-confirm-content {
          font-family: "DM Sans", sans-serif !important;
        }

        .ant-drawer-body {
          padding: 0 !important;
          background: linear-gradient(180deg, var(--sn-bg-2), var(--sn-bg-1)) !important;
          display: flex;
          flex-direction: column;
        }

        .ant-drawer-header {
          background: linear-gradient(180deg, var(--sn-bg-2), var(--sn-bg-1)) !important;
          border-color: rgba(255,255,255,0.08) !important;
        }

        .ant-drawer-title {
          color: rgba(255,255,255,0.88) !important;
          font-family: "DM Sans", sans-serif !important;
          font-weight: 700 !important;
        }

        .ant-drawer-close {
          color: rgba(255,255,255,0.55) !important;
        }

        .ant-affix {
          z-index: 1500;
        }
      `}</style>

      {/* ─── FIXED SIDEBAR (desktop) ─── */}
      {!isMobile && (
        <div className={`sn-fixed-sidebar${collapsed ? " collapsed" : ""}`}>
          {sidebarContent}
        </div>
      )}

      {/* ─── MOBILE DRAWER ─── */}
      {isMobile && (
        <Drawer
          placement="left"
          closable
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={272}
          styles={{ body: { padding: 0, display: "flex", flexDirection: "column", background: "linear-gradient(180deg, var(--sn-bg-2), var(--sn-bg-1))" } }}
          title="Navigation"
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* ─── MAIN CONTENT (pushed right by sidebar width) ─── */}
      <div
        className="sn-main-wrapper"
        style={{ marginLeft: isMobile ? 0 : sidebarWidth }}
      >
        {/* Sticky Header */}
        <div className="sn-header">
          <div className="sn-header-left">
            <button
              className="sn-toggle-btn"
              onClick={() => (isMobile ? setDrawerVisible(true) : setCollapsed(!collapsed))}
            >
              {isMobile ? (
                <MenuUnfoldOutlined style={{ fontSize: 17 }} />
              ) : collapsed ? (
                <MenuUnfoldOutlined style={{ fontSize: 17 }} />
              ) : (
                <MenuFoldOutlined style={{ fontSize: 17 }} />
              )}
            </button>
          </div>

          <div className="sn-header-right">
            <Tooltip title="Notifications">
              <div className="sn-notif-btn">
                <NotificationBell />
              </div>
            </Tooltip>

            <div className="sn-header-divider" />

            {isLoggedIn ? (
              <Dropdown
  menu={userMenu}
  trigger={["click"]}
  placement="bottomRight"
  overlayStyle={{ zIndex: 9999 }}
  getPopupContainer={() => document.body}
>
                <div className="sn-profile-btn">
                  <Avatar size={32} src="/profile.png" alt="Profile" />
                  {!isMobile && (
                    <div className="sn-profile-info">
                      <span className="sn-profile-name-sm">{displayName}</span>
                      <span className="sn-profile-role-sm">{roleLabel}</span>
                    </div>
                  )}
                  <DownOutlined style={{ fontSize: 10, color: "#94a3b8", marginLeft: 2 }} />
                </div>
              </Dropdown>
            ) : (
              <Button
                type="primary"
                icon={<LoginOutlined />}
                onClick={() => navigate("/login")}
                style={{
                  background: "linear-gradient(135deg, #c9a84c, #e8c96a)",
                  border: "none",
                  borderRadius: "12px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: "12px",
                  letterSpacing: "0.04em",
                  boxShadow: "0 10px 24px rgba(201,168,76,0.25)",
                }}
              >
                Login
              </Button>
            )}
          </div>
        </div>

        {/* Page Content */}
        <div className="sn-content-area">
          <div className="sn-content-inner">
            <Outlet key={location.pathname} />
          </div>
        </div>
      </div>

      {/* Chat Widget */}
      {(isSuperAdminOrAdmin || isProdRole) && <ChatWidget visible={true} />}

      {/* Logout Modal */}
      <Modal
        title={
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: "15px" }}>
            Confirm Logout
          </span>
        }
        open={logoutModalVisible}
        onOk={handleLogout}
        onCancel={() => setLogoutModalVisible(false)}
        okText="Yes, Logout"
        cancelText="Cancel"
        okButtonProps={{
          style: {
            background: "linear-gradient(135deg, #c9a84c, #e8c96a)",
            border: "none",
            borderRadius: "10px",
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
          },
        }}
        cancelButtonProps={{
          style: {
            borderRadius: "10px",
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 600,
          },
        }}
      >
        <p style={{ fontFamily: "'DM Sans', sans-serif", color: "#475569" }}>
          Are you sure you want to log out of your session?
        </p>
      </Modal>
    </>
  );
};

export default Dashboard;
